import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import apiendpoints from "../../apiendpoints.json";
import { getAuthHeaders, hasPermission } from "../../utils/auth";

const emptyForm = { accountNo: "", accountName: "", ifsc: "", upiId: "", bankName: "" };

const BankManagement = () => {
  const [details, setDetails] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canCreate = hasPermission("bank_details", "create");
  const canUpdate = hasPermission("bank_details", "update");
  const canDelete = hasPermission("bank_details", "delete");

  const loadDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiendpoints.equityUrl}/api/bank-details`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Could not load bank details.");
      setDetails(result.payload || []);
    } catch (error) {
      toast.error(error.message || "Could not load bank details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
  }, []);

  const updateField = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const saveDetail = async (event) => {
    event.preventDefault();
    if (editingId && !canUpdate) {
      return toast.error("You do not have permission to update bank details.");
    }
    if (!editingId && !canCreate) {
      return toast.error("You do not have permission to create bank details.");
    }
    setSaving(true);
    try {
      const response = await fetch(
        `${apiendpoints.equityUrl}/api/bank-details${editingId ? `/${editingId}` : ""}`,
        {
          method: editingId ? "PUT" : "POST",
          headers: getAuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(form),
        }
      );
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Could not save bank detail.");
      toast.success(editingId ? "Bank detail updated." : "Bank detail added.");
      resetForm();
      await loadDetails();
    } catch (error) {
      toast.error(error.message || "Could not save bank detail.");
    } finally {
      setSaving(false);
    }
  };

  const deleteDetail = async (id) => {
    if (!canDelete) {
      return toast.error("You do not have permission to delete bank details.");
    }
    if (!window.confirm("Delete this bank detail?")) return;
    try {
      const response = await fetch(`${apiendpoints.equityUrl}/api/bank-details/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Could not delete bank detail.");
      toast.success("Bank detail deleted.");
      await loadDetails();
    } catch (error) {
      toast.error(error.message || "Could not delete bank detail.");
    }
  };

  return (
    <section className="min-h-screen bg-white p-4 sm:p-8">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between border-b pb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Bank Management</h1>
            <p className="mt-1 text-sm text-gray-500">Shared bank accounts</p>
          </div>
          {editingId && <button type="button" onClick={resetForm} className="text-sm text-gray-500 underline">Cancel edit</button>}
        </div>

        {((!editingId && canCreate) || (editingId && canUpdate)) && (
          <form onSubmit={saveDetail} className="mt-5 grid gap-3 rounded-xl border bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              ["accountNo", "Account Number"],
              ["accountName", "Account Holder Name"],
              ["ifsc", "IFSC"],
              ["upiId", "UPI ID"],
              ["bankName", "Bank Name"],
            ].map(([name, label]) => (
              <label key={name} className="text-xs font-semibold text-gray-600">
                {label}
                <input name={name} value={form[name]} onChange={updateField} required={name !== "upiId"} className="mt-1 h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm font-normal outline-none focus:border-blue-500" />
              </label>
            ))}
            <button type="submit" disabled={saving} className="h-10 self-end rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? "Saving..." : editingId ? "Update" : "Add bank detail"}
            </button>
          </form>
        )}

        <div className="mt-6 overflow-x-auto rounded-xl border">
          {loading ? <p className="p-5 text-sm text-gray-500">Loading...</p> : details.length === 0 ? <p className="p-5 text-sm text-gray-500">No bank details for this entity.</p> : (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Bank Name</th>
                  <th className="px-4 py-3">Account Holder Name</th>
                  <th className="px-4 py-3">IFSC</th>
                  <th className="px-4 py-3">UPI ID</th>
                  {(canUpdate || canDelete) && <th className="px-4 py-3">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {details.map((detail) => (
                  <tr key={detail.id} className="text-gray-700">
                    <td className="px-4 py-3">{detail.bankName}</td>
                    <td className="px-4 py-3">{detail.accountName}<br /><span className="text-xs text-gray-400">{detail.accountNo}</span></td>
                    <td className="px-4 py-3">{detail.ifsc}</td>
                    <td className="px-4 py-3">{detail.upiId || "-"}</td>
                    {(canUpdate || canDelete) && (
                      <td className="px-4 py-3">
                        {canUpdate && (
                          <button type="button" onClick={() => { setEditingId(detail.id); setForm({ accountNo: detail.accountNo, accountName: detail.accountName, ifsc: detail.ifsc, upiId: detail.upiId || "", bankName: detail.bankName }); }} className="mr-3 text-blue-600">
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" onClick={() => deleteDetail(detail.id)} className="text-red-600">
                            Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </section>
  );
};

export default BankManagement;

